<i18n>
{
  "fr": {
    "configuratorCustomizationSummary": {
      "informations": {
        "title": "Votre produit",
        "label": {
          "model": "Format",
          "quantity": "Quantité",
          "theme": "Votre visuel",
          "textCustomization": "Personnalisation texte {0}"
        }
      },
      "product": {
        "title": "Visuel et personnalisation",
        "label": {
          "expressMode": "Réalisation express",
          "price": "Tarif TTC",
          "price_without_vat": "Tarif HT"
        },
        "value": {
          "expressMode": {
            "yes": "Oui",
            "no": "Non"
          },
          "price": "{unitPrice}/gobelet soit {price}",
          "totalPrice": "{price} € TTC",
          "unitPrice": "{price} € TTC/gobelet"
        },
        "customizationType": {
          "text": "Texte",
          "image": "Image"
        }
      }
    }
  }
}

</i18n>

<template>
  <div class="configurator-customization-summary">
    <div class="configurator-customization-summary__aside">
      <div class="configurator-customization-summary__thumbnail-wrapper">
        <template
          v-if="
            thumbnails &&
            thumbnails.center &&
            thumbnails.left &&
            thumbnails.right &&
            thumbnails.back
          "
        >
          <img
            v-if="thumbnails && thumbnails.right"
            :src="thumbnails.right"
            alt=""
            class="configurator-customization-summary__thumbnail"
          />
          <img
            v-if="thumbnails && thumbnails.center"
            :src="thumbnails.center"
            alt=""
            class="configurator-customization-summary__thumbnail"
          />
          <img
            v-if="thumbnails && thumbnails.left"
            :src="thumbnails.left"
            alt=""
            class="configurator-customization-summary__thumbnail"
          />
          <img
            v-if="thumbnails && thumbnails.back"
            :src="thumbnails.back"
            alt=""
            class="configurator-customization-summary__thumbnail"
          />
        </template>
        <template v-else>
          <LgfSpinner class="configurator-customization-summary__spinner" />
        </template>
      </div>
    </div>

    <div class="configurator-customization-summary__body">
      <div class="configurator-customization-summary__summary-card">
        <div class="configurator-customization-summary__title-wrapper">
          <div
            class="configurator-customization-summary__title configurator-customization-summary__title--product"
          >
            {{ $t('configuratorCustomizationSummary.informations.title') }}
          </div>
          <div class="configurator-customization-summary__price-wrapper">
            <span>{{
              $t('configuratorCustomizationSummary.product.value.totalPrice', {
                price: price,
              })
            }}</span>
            <span>{{
              $t('configuratorCustomizationSummary.product.value.unitPrice', {
                price: unitPrice,
              })
            }}</span>
          </div>
        </div>
        <dl class="configurator-customization-summary__detail-list">
          <template v-for="(item, index) in informationList">
            <dt
              :key="`information-label--${index}`"
              class="configurator-customization-summary__label"
            >
              {{ item.label }}
            </dt>
            <dd
              :key="`information-value--${index}`"
              class="configurator-customization-summary__value"
            >
              {{ item.value }}
            </dd>
          </template>
        </dl>
      </div>

      <div class="configurator-customization-summary__summary-card">
        <div class="configurator-customization-summary__title-wrapper">
          <div
            class="configurator-customization-summary__title configurator-customization-summary__title--content"
          >
            {{ $t('configuratorCustomizationSummary.product.title') }}
          </div>
        </div>
        <dl class="configurator-customization-summary__detail-list">
          <template v-for="(item, index) in customizationsList">
            <dt
              :key="`product-label--${index}`"
              class="configurator-customization-summary__label"
            >
              {{ item.label }}
            </dt>
            <dd
              :key="`product-value--${index}`"
              class="configurator-customization-summary__value"
            >
              {{ item.value }}
            </dd>
          </template>
        </dl>
      </div>
    </div>
  </div>
</template>

<script>
import { ZoneType } from '../../api/static/zones';
import LgfSpinner from '../LgfSpinner.vue';

export default {
  name: 'ConfiguratorCustomizationSummary',
  components: {
    LgfSpinner,
  },
  props: {
    color: {
      type: String,
      default: undefined,
    },
    size: {
      type: String,
      default: undefined,
    },
    product: {
      type: String,
      default: undefined,
    },
    customizations: {
      type: Array,
      default: () => [],
    },
    quantity: {
      type: String,
      default: undefined,
    },
    expressMode: {
      type: Boolean,
    },
    unitPrice: {
      type: Number,
      default: undefined,
    },
    price: {
      type: Number,
      default: undefined,
    },
    priceWithoutVat: {
      type: Number,
      default: undefined,
    },
    unitPriceWithoutVat: {
      type: Number,
      default: undefined,
    },
    thumbnails: {
      type: Object,
      default: () => ({
        center: undefined,
        back: undefined,
        left: undefined,
        right: undefined,
      }),
    },
  },
  computed: {
    informationList() {
      const list = [];

      if (this.size && this.color) {
        list.push({
          label: this.$t(
            'configuratorCustomizationSummary.informations.label.model'
          ),
          value: `${this.size} - ${this.color}`,
        });
      }

      if (this.quantity) {
        list.push({
          label: this.$t(
            'configuratorCustomizationSummary.informations.label.quantity'
          ),
          value: this.quantity,
        });
      }

      if (this.product) {
        list.push({
          label: this.$t(
            'configuratorCustomizationSummary.informations.label.theme'
          ),
          value: this.product,
        });
      }

      return list;
    },
    customizationsList() {
      let lastUsedImageIndex = 0;
      let lastUsedTextIndex = 0;

      const list = [];

      this.customizations.forEach(({ zone, customization }) => {
        let displayIndex = 0;

        if (zone.type === ZoneType.IMAGE) {
          lastUsedImageIndex += 1;
          displayIndex = lastUsedImageIndex;
        } else {
          lastUsedTextIndex += 1;
          displayIndex = lastUsedTextIndex;
        }

        if (zone.type === ZoneType.TEXT) {
          list.push({
            label:
              this.$t(
                `configuratorCustomizationSummary.product.customizationType.${zone.type}`
              ) + ` ${displayIndex}`,
            value: customization.text || zone.defaultValue,
          });
        } else {
          list.push({
            label:
              this.$t(
                `configuratorCustomizationSummary.product.customizationType.${zone.type}`
              ) + ` ${displayIndex}`,
            value: this.$t(
              `configuratorCustomizationSummary.product.customizationType.image`
            ),
          });
        }
      });
      return list;
    },
  },
  beforeDestroy() {
    if (this.thumbnails) {
      Object.values(this.thumbnails).forEach(url => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.configurator-customization-summary {
  $block-selector: &;
  display: flex;
  align-items: center;
  flex-direction: column;

  &__aside {
    flex: none;
    display: flex;
    gap: 0.5em;
    max-width: 100%;
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    width: 100%;

    > *:last-child {
      margin-bottom: 0;
    }
  }

  &__summary-card {
    background-color: $color-white;
    padding: 0.9375rem;
    border-radius: 0.75rem;
    width: 100%;
  }

  &__title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    text-transform: uppercase;
    font-weight: bold;
    color: $color-blue-dodger-blue;
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-family: $font-big-shoulders;

    &--product {
      &::before {
        @extend %icon;
        @extend %icon--tiny-cup;

        display: inline;
      }
    }

    &--content {
      &::before {
        @extend %icon;
        @extend %icon--customization;

        display: inline;
      }
    }
  }

  &__thumbnail-wrapper {
    height: 13.75rem;
    display: flex;
    max-width: 100%;
  }

  &__spinner {
    width: 5rem;
    height: 12rem;
  }

  &__price-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    font-size: 1rem;
    color: $color-blue-madison;
    font-weight: 400;
    line-height: 1rem;

    & > *:first-child {
      font-weight: bold;
    }
  }

  &__label {
    font-weight: 400;
    display: inline;
  }

  &__value {
    margin-left: 0;
    display: inline;
    font-weight: 600;

    &:first-letter {
      text-transform: uppercase;
    }

    &::after {
      content: '';
      display: block;
    }
  }

  &__thumbnail {
    max-width: 8em;
    max-height: 14em;
    flex: 1;
    flex-shrink: 1;
    min-width: 0;
    object-fit: contain;
  }

  &__detail-list {
    margin: 0;
    color: $color-arapawa;
  }
}
</style>
